import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faEyeSlash, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { CsvSource } from '@/types';

interface CsvMappingForm {
  product_property: 'sku' | 'title' | 'disposable_quantity' | 'price' | '';
  csv_header: string;
  csv_source_id?: number;
  id?: number;
}

const CsvSourceView: React.FC = () => {
  const emptyForm = {
    source_url: '',
    username: '',
    password: '',
  };
  const [formData, setFormData] = useState(emptyForm);
  const [csvSourceId, setCsvSourceId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [urlError, setUrlError] = useState<string>('');
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<CsvMappingForm[]>([]);

  const queryClient = useQueryClient();

  // Fetch existing CSV source configuration
  const {
    data: csvSourceData,
    isLoading: isCsvSourceLoading,
    isError,
    error,
    refetch,
  } = useQuery('csvSource', apiService.getCsvSource, {
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    if (csvSourceData) {
      setCsvSourceId(csvSourceData.id);
      setFormData({
        source_url: csvSourceData.source_url || '',
        username: csvSourceData.username || '',
        password: csvSourceData.password || '',
      });
      
      // Load mappings from response
      if (csvSourceData.csv_mappings && csvSourceData.csv_mappings.length > 0) {
        const formMappings: CsvMappingForm[] = csvSourceData.csv_mappings.map(mapping => ({
          product_property: mapping.product_property as 'sku' | 'title' | 'disposable_quantity' | 'price' | '',
          csv_header: mapping.csv_header ?? "",   // <-- FIXED
        }));
        
        setMappings(formMappings);
        
        // Extract unique headers from mappings to populate parsedHeaders
        const headers = [
          ...new Set(
            csvSourceData.csv_mappings
              .map(m => m.csv_header)
              .filter((h): h is string => typeof h === "string")
          )
        ];
        setParsedHeaders(headers);
        localStorage.setItem('csv_parsed_headers', JSON.stringify(headers));
      } else {
        setMappings([]);
      }
      
      // Load saved parsed headers from localStorage if they exist and no mappings loaded
      if (!csvSourceData.csv_mappings || csvSourceData.csv_mappings.length === 0) {
        const savedHeaders = localStorage.getItem('csv_parsed_headers');
        if (savedHeaders) {
          try {
            const headers = JSON.parse(savedHeaders);
            setParsedHeaders(headers);
          } catch (e) {
            console.error('Failed to parse saved headers:', e);
          }
        }
      }
    } else {
      // Reset id when no data
      setCsvSourceId(null);
    }
  }, [csvSourceData]);

  // Validate URL
  const isValidUrl = (url: string): boolean => {
    if (!url || url.trim() === '') return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Handle URL input change
  const handleUrlChange = (value: string) => {
    setFormData(prev => ({ ...prev, source_url: value }));
    if (value && !isValidUrl(value)) {
      setUrlError('Please enter a valid URL');
    } else {
      setUrlError('');
    }
  };

  // Parse headers mutation
  const parseHeadersMutation = useMutation(
    () => apiService.parseHeaders({
      source_url: formData.source_url,
      username: formData.username || undefined,
      password: formData.password || undefined,
    }),
    {
      onSuccess: (headers: string[]) => {
        // Save parsed headers to state and localStorage
        setParsedHeaders(headers);
        localStorage.setItem('csv_parsed_headers', JSON.stringify(headers));
        alert('Headers parsed successfully');
      },
      onError: (error: any) => {
        console.error('Parse headers failed:', error);
        alert('Failed to parse headers: ' + (error?.response?.data?.message || error?.message || 'Unknown error'));
      },
    }
  );

  // Create or Update CSV source mutation
  const saveMutation = useMutation(
    (data: Partial<CsvSource> & { csv_mappings?: CsvMappingForm[] }) => {
      if (csvSourceId) {
        return apiService.updateCsvSource(data);
      } else {
        return apiService.createCsvSource(data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('csvSource');
        alert(csvSourceId ? 'CSV Source updated successfully' : 'CSV Source created successfully');
        refetch();
      },
      onError: (error: any) => {
        console.error('Save failed:', error);
        alert('Failed to save CSV Source: ' + (error?.response?.data?.message || error?.message || 'Unknown error'));
      },
    }
  );

  const deleteMutation = useMutation(
    () => apiService.deleteCsvSource(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('csvSource');
        setFormData(emptyForm);
        setCsvSourceId(null);
        setMappings([]);
        setParsedHeaders([]);
        alert('CSV Source deleted successfully');
      },
      onError: (error: any) => {
        console.error('Delete failed:', error);
        alert('Failed to delete CSV Source: ' + (error?.response?.data?.message || error?.message || 'Unknown error'));
      },
    }
  );

  const handleInputChange = (field: string, value: string) => {
    if (field === 'source_url') {
      handleUrlChange(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleParseHeaders = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isValidUrl(formData.source_url)) {
      parseHeadersMutation.mutate();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidUrl(formData.source_url)) {
      setUrlError('Please enter a valid URL');
      return;
    }

    const submitData: Partial<CsvSource> & { csv_mappings?: CsvMappingForm[] } = {
      source_url: formData.source_url,
      username: formData.username || '',
      password: formData.password || '',
      csv_mappings: mappings.length > 0 ? mappings : undefined,
    };

    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this CSV Source?')) {
      deleteMutation.mutate();
    }
  };

  const handleAddMapping = () => {
    setMappings(prev => [...prev, { product_property: '', csv_header: '' }]);
  };

  const handleMappingChange = (index: number, field: keyof CsvMappingForm, value: string) => {
    setMappings(prev => prev.map((mapping, i) => 
      i === index ? { ...mapping, [field]: value } : mapping
    ));
  };

  const handleRemoveMapping = (index: number) => {
    setMappings(prev => prev.filter((_, i) => i !== index));
  };

  const isParseButtonEnabled = isValidUrl(formData.source_url);
  const isSaveButtonEnabled = isValidUrl(formData.source_url);

  if (isCsvSourceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  // Check if error is 404 - if so, show empty form instead of error
  const is404Error = error && (error as any)?.response?.status === 404;
  
  if (isError && !is404Error) {
    return (
      <div className="space-y-4">
        <p className="text-red-600">Failed to load CSV Source configuration.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">CSV Source</h1>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">
              <div className="form-column">
                <div>
                  <label className="form-label form-label-required">Source URL:</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="url"
                      value={formData.source_url}
                      onChange={(e) => handleInputChange('source_url', e.target.value)}
                      className={`form-input flex-1 ${urlError ? 'border-red-500' : ''}`}
                      required
                      placeholder="https://example.com/data.csv"
                    />
                    <button
                      type="button"
                      onClick={handleParseHeaders}
                      disabled={!isParseButtonEnabled || parseHeadersMutation.isLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 whitespace-nowrap"
                    >
                      {parseHeadersMutation.isLoading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                          <span>Parsing...</span>
                        </>
                      ) : (
                        <span>Parse Headers</span>
                      )}
                    </button>
                  </div>
                  {urlError && (
                    <p className="mt-1 text-sm text-red-600">{urlError}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Username:</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="form-input"
                    placeholder="Enter username (optional)"
                  />
                </div>

                <div>
                  <label className="form-label">Password:</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="form-input pr-10"
                      placeholder="Enter password (optional)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        className="w-4 h-4"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mappings Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Mappings</h2>
                <button
                  type="button"
                  onClick={handleAddMapping}
                  disabled={parsedHeaders.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                  <span>Add mapping</span>
                </button>
              </div>

              {mappings.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product Property
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          CSV Header
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {mappings.map((mapping, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={mapping.product_property}
                              onChange={(e) => handleMappingChange(index, 'product_property', e.target.value)}
                              className="form-input w-full"
                            >
                              <option value="">Select property</option>
                              <option value="sku">SKU</option>
                              <option value="title">Title</option>
                              <option value="disposable_quantity">Disposable Quantity</option>
                              <option value="price">Price</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={mapping.csv_header}
                              onChange={(e) => handleMappingChange(index, 'csv_header', e.target.value)}
                              className="form-input w-full"
                              disabled={parsedHeaders.length === 0}
                            >
                              <option value="">Select CSV header</option>
                              {parsedHeaders.length > 0 ? (
                                parsedHeaders.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled>No headers available. Please parse headers first.</option>
                              )}
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              type="button"
                              onClick={() => handleRemoveMapping(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {mappings.length === 0 && (
                <p className="text-gray-500 text-sm italic">No mappings added yet. Click "Add mapping" to create one.</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end pt-6 space-x-3">
              <button
                type="submit"
                disabled={!isSaveButtonEnabled || saveMutation.isLoading}
                className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              >
                {saveMutation.isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save</span>
                )}
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CsvSourceView;

